import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTheme, buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import ListItem from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/ListItem';
import { noop } from 'Helpers/PropTypes';
import Badge from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Badge';
import { scrollToRef } from 'Helpers/ScrollToRef';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import fr from 'facet-rest';
import {
  ThreadListItemAvatar,
} from 'Views/Page/Content/Templates/MessagesPage/components/MessagesPageContent/components/ThreadsSection/components/ThreadList/components/ThreadListItem/components/ThreadListItemAvatar';
import {
  ThreadListItemContent,
} from 'Views/Page/Content/Templates/MessagesPage/components/MessagesPageContent/components/ThreadsSection/components/ThreadList/components/ThreadListItem/components/ThreadListItemContent';
import {
  ThreadListItemDate,
} from 'Views/Page/Content/Templates/MessagesPage/components/MessagesPageContent/components/ThreadsSection/components/ThreadList/components/ThreadListItem/components/ThreadListItemDate';
import {
  ThreadListItemQuickActions,
} from 'Views/Page/Content/Templates/MessagesPage/components/MessagesPageContent/components/ThreadsSection/components/ThreadList/components/ThreadListItem/components/ThreadListItemQuickActions';
import Box
  from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import useMobileLayout from 'Hooks/useMobileLayout';
import { DataValueMap } from 'Helpers/Testing';


const useStyles = buildUseStyles('ThreadListItem', (theme) => ({
  root: {
    padding: theme.spacing(0.5, 0),
  },
  listItem: {
    padding: theme.spacing(1, 1.5),
    gap: theme.spacing(2),
    borderRadius: theme.radius.md,
    '&:hover': {
      backgroundColor: theme.palette.facet.secondary12p,
    },
  },
  listItemSelected: {
    '&.Mui-selected': {
      backgroundColor: theme.palette.facet.secondary30p,
      '&:hover': {
        backgroundColor: theme.palette.facet.secondary30p,
      },
    },
    borderRadius: theme.radius.md,
  },
  unreadBadge: {
    top: '20%',
    left: '4%',
  },
  unreadBadgeRoot: {
    display: 'flex',
  },
  listItemRightContainer: {
    display: 'flex',
    maxHeight: 60,
  },
  listItemRightContainerDesktop: {
    flexDirection: 'column',
    paddingBottom: theme.spacing(5),
  },
  listItemRightContainerMobile: {
    flexDirection: 'row',
    paddingBottom: theme.spacing(0),
  },
}));

const ThreadListItem = ({
  className,
  threadResource,
  selected,
  onClick,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const [isHovered, setIsHovered] = React.useState(false);
  const isMobile = useMobileLayout();

  const listItemRef = useRef();
  const isUnread = threadResource.isTruthy('isUnread');

  useComponentDidMount(() => {
    if (selected) {
      scrollToRef(listItemRef);
    }
  });


  return (
    <div ref={listItemRef} className={classNames(className, classes.root)}>
      <Badge
        classes={{
          badge: classes.unreadBadge,
          root: classes.unreadBadgeRoot,
        }}
        overlap={'circle'}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        variant={'dot'}
        color={'alertRed'}
        invisible={!isUnread}
        {...DataValueMap
          .init('messages-page-thread-list-unread-badge')
          .append(DataValueMap.keyMap.dataResourceId, threadResource.id())
          .build()}
      >
        <ListItem
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          button
          className={classNames(classes.listItem)}
          onClick={() => onClick()}
          selected={selected}
          classes={{
            selected: classes.listItemSelected,
          }}
          {...DataValueMap
            .init('messages-page-thread-list-item')
            .append(DataValueMap.keyMap.dataResourceId, threadResource.id())
            .append(DataValueMap.keyMap.dataType, isUnread ? 'unread' : 'read')
            .append(DataValueMap.keyMap.dataFavorite, threadResource.attr('isStarred'))
            .append(DataValueMap.keyMap.dataArchived, threadResource.attr('isArchived'))
            .build()}
        >
          <ThreadListItemAvatar
            threadResource={threadResource}
          />
          <ThreadListItemContent
            threadResource={threadResource}
          />
          <Box className={classNames(classes.listItemRightContainer,
            {
              [classes.listItemRightContainerDesktop]: !isMobile,
              [classes.listItemRightContainerMobile]: isMobile,
            })}
          >
            <ThreadListItemDate
              threadResource={threadResource}
            />
            <ThreadListItemQuickActions
              threadResource={threadResource}
              isHovered={isHovered}
            />
          </Box>
        </ListItem>
      </Badge>
    </div>
  );
};

ThreadListItem.defaultProps = {
  className: '',
  selected: false,
  onClick: noop,
};

ThreadListItem.propTypes = {
  className: PropTypes.string,
  threadResource: PropTypes.instanceOf(fr.Resource).isRequired,
  selected: PropTypes.bool,
  onClick: PropTypes.func,
};

export { ThreadListItem };
